export const DRAFT_PUBLISHED_ERROR = "Drafts are only available before a product is published.";
export const DRAFT_DUPLICATE_NAME_ERROR = "A product with this name already exists within your organization.";
export const DRAFT_SAVE_GENERIC_ERROR = "Your draft product was not saved. Please review the form and try again.";

export async function runSaveDraftProductFlow({
  workingProductId,
  product,
  hyperlink,
  loadedHyperlink,
  vendorId,
  // Owner payload for draft create: `{ vendorId }` or `{ agencyId }`. Falls back to the
  // legacy `vendorId` arg when not provided.
  ownerPayload,
  buildProductSavePayload,
  saveDraftProduct,
  updateProduct,
  validateDraftHyperlink,
  hasAnyHyperlinkInput,
  buildHyperlinkPayloadForApi,
  hasHyperlinkChanges,
  updateHyperlink,
  createHyperlinkForProduct,
  productNameRequiredMessage,
}) {
  if (workingProductId && product?.isPublished === true) {
    return { ok: false, type: "inline_error", field: "form.general", message: DRAFT_PUBLISHED_ERROR };
  }

  if (!product.name?.trim()) {
    return {
      ok: false,
      type: "validation_errors",
      errors: { "product.name": productNameRequiredMessage },
    };
  }

  const draftHyperlinkValidation = validateDraftHyperlink(hyperlink);
  if (draftHyperlinkValidation) {
    return {
      ok: false,
      type: "validation_errors",
      errors: { [draftHyperlinkValidation.field]: draftHyperlinkValidation.message },
    };
  }

  try {
    const payload = { ...buildProductSavePayload(product), ...(ownerPayload ?? { vendorId }), isPublished: false, isActive: true };
    const draftResponse = workingProductId
      ? await updateProduct(workingProductId, payload)
      : await saveDraftProduct(payload);

    if (!draftResponse || draftResponse.status >= 300) {
      if (draftResponse?.status === 409) {
        return { ok: false, type: "inline_error", field: "product.name", message: DRAFT_DUPLICATE_NAME_ERROR };
      }
      return { ok: false, type: "inline_error", field: "form.general", message: DRAFT_SAVE_GENERIC_ERROR };
    }

    const savedDraftId = draftResponse?.data?.id;
    const resolvedDraftId = workingProductId || savedDraftId;
    let nextLoadedHyperlink = null;

    if (resolvedDraftId && hasAnyHyperlinkInput(hyperlink)) {
      const hyperlinkPayload = buildHyperlinkPayloadForApi(hyperlink);
      if (hyperlink.id) {
        if (hasHyperlinkChanges(hyperlink, loadedHyperlink)) {
          const updateHyperlinkResponse = await updateHyperlink(hyperlink.id, { ...hyperlinkPayload, productId: resolvedDraftId });
          if (updateHyperlinkResponse.status > 299) {
            return {
              ok: false,
              type: "inline_error",
              field: "hyperlink.url",
              reason: "acr-link-upload-failed",
            };
          }
          nextLoadedHyperlink = { ...hyperlink };
        }
      } else {
        const createHyperlinkResult = await createHyperlinkForProduct(resolvedDraftId, hyperlinkPayload);
        if (!createHyperlinkResult.ok) {
          return {
            ok: false,
            type: "inline_error",
            field: "hyperlink.url",
            reason: createHyperlinkResult.reason ?? "acr-link-upload-failed",
          };
        }
      }
    }

    return {
      ok: true,
      resolvedDraftId,
      shouldSetWorkingProductId: Boolean(!workingProductId && resolvedDraftId),
      nextLoadedHyperlink,
    };
  } catch (e) {
    if (e?.response?.status === 409) {
      return { ok: false, type: "inline_error", field: "product.name", message: DRAFT_DUPLICATE_NAME_ERROR };
    }
    return { ok: false, type: "inline_error", field: "form.general", message: DRAFT_SAVE_GENERIC_ERROR };
  }
}
